import Axios from 'axios'
import { apiUrls, getParams, setAuthorizationHeaders, setHeaders } from '../utils'
import { getCoordinates } from '@/utils/getLatestLocation'

const { newOOBaseUrl } = apiUrls

interface StoreApiResponse<T = any> {
  data: T
  status: number
  statusText: string
  headers: any
  config: any
}

interface StoreApis {
  getStores: (params?: { storeTypeCode?: string, nextUrl?: string }) => Promise<StoreApiResponse>
  getStoreDetails: (storeId: string | number, serverCoords?: any) => Promise<StoreApiResponse>
  storeCategoryProducts: (storeId: string | number) => Promise<StoreApiResponse>
  storeCategoryAllProducts: (storeId: string | number, categoryId: string | number) => Promise<StoreApiResponse>
  storeCategorySubCategories: (storeId: string | number, categoryId: string | number) => Promise<StoreApiResponse>
  storeRepeatProducts: (storeId: string | number) => Promise<StoreApiResponse>
  storeBrandBanner: () => Promise<StoreApiResponse>
  shopFrontSearchHistory: (storeId: string | number) => Promise<StoreApiResponse>
  shopFrontSearch: (storeId: string | number, searchText: string | number) => Promise<StoreApiResponse>
  shopFrontSearchAdd: (storeId: string | number, searchText: string | number) => Promise<StoreApiResponse>
  spotLightProducts: (storeId: string | number) => Promise<StoreApiResponse>
  getRedirectionStoreDetails: (storeId: string | number) => Promise<StoreApiResponse>
  getStoreCoupons: (storeId: string | number, page?: number, limit?: number) => Promise<StoreApiResponse>
}

const storeApis: StoreApis = {

  getStores: async ({
    storeTypeCode = '',
    nextUrl = ''
  } = {}) => {
    const stores = `${newOOBaseUrl}customer/home/stores`
    const {
      latitude,
      longitude
    } = getCoordinates()
    return await Axios({
      url: stores,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: getParams({
        latitude,
        longitude,
        ...(nextUrl !== '' ? { nextUrl } : {}),
        ...(storeTypeCode !== '' ? { storeTypeCode } : {})
      })
    })
  },

  getStoreDetails: async (storeId, serverCoords) => {
    const storeProfile = `${newOOBaseUrl}store/store-aux/${storeId}`
    const {
      latitude,
      longitude
    } = getCoordinates(serverCoords)
    return await Axios({
      url: storeProfile,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: getParams({
        latitude,
        longitude
      })
    })
  },

  storeCategoryProducts: async (storeId) => {
    const storeProducts = `${newOOBaseUrl}store/shop-front-open-category/${storeId}`
    const {
      latitude,
      longitude
    } = getCoordinates()
    return await Axios({
      url: storeProducts,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: getParams({
        latitude,
        longitude
      })
    })
  },

  storeCategoryAllProducts: async (storeId, categoryId) => {
    const storeProducts = `${newOOBaseUrl}store/store-products-v2/${storeId}?commonCategoryId=${categoryId}`
    return await Axios({
      url: storeProducts,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders())
      // params: getParams({ latitude, longitude })
    })
  },

  storeCategorySubCategories: async (storeId, categoryId) => {
    const storeProducts = `${newOOBaseUrl}store/store-all-category-v3/${storeId}?categoryId=${categoryId}`
    return await Axios({
      url: storeProducts,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders())
      // params: getParams({ latitude, longitude })
    })
  },

  storeRepeatProducts: async (storeId) => {
    const repeatProducts = `${newOOBaseUrl}store/shop-front/v2/repeat-order-products/${storeId}`
    const {
      latitude,
      longitude
    } = getCoordinates()
    return await Axios({
      url: repeatProducts,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: getParams({
        latitude,
        longitude
      })
    })
  },

  storeBrandBanner: async () => {
    const brandBanner = `${newOOBaseUrl}customer/home/banners/brand`
    const {
      latitude,
      longitude
    } = getCoordinates()
    return await Axios({
      url: brandBanner,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: getParams({
        latitude,
        longitude,
        section: 'GROWTH',
        page_name: 'SHOPFRONT_PAGE'
      })
    })
  },

  shopFrontSearchHistory: async (storeId) => {
    const {
      latitude,
      longitude
    } = getCoordinates()
    const searchHistory = `${newOOBaseUrl}search/shop-front-search-history`
    return await Axios({
      url: searchHistory,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: getParams({
        latitude,
        longitude,
        storeId
      })
    })
  },

  shopFrontSearch: async (storeId, searchText) => {
    const searchProduct = `${newOOBaseUrl}store/shop-front-products/${storeId}`
    return await Axios({
      url: searchProduct,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: getParams({
        // latitude,
        // longitude,
        searchText
      })
    })
  },

  shopFrontSearchAdd: async (storeId, searchText) => {
    const searchProduct = `${newOOBaseUrl}customer/search/add`
    const {
      latitude,
      longitude
    } = getCoordinates()
    return await Axios({
      url: searchProduct,
      method: 'POST',
      headers: setHeaders(setAuthorizationHeaders()),
      data: {
        storeId,
        searchTerm: searchText,
        searchType: 'PRODUCT',
        latitude,
        longitude,
        isCompleted: true
      }
    })
  },

  spotLightProducts: async (storeId) => {
    const spotLightProducts = `${newOOBaseUrl}store/shop-front/spotlight-products/${storeId}`
    const {
      latitude,
      longitude
    } = getCoordinates()
    return await Axios({
      url: spotLightProducts,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: getParams({
        latitude,
        longitude
      })
    })
  },

  getRedirectionStoreDetails: async (storeId) => {
    const storeProfile = `${newOOBaseUrl}store/store-aux/${storeId}`
    const {
      latitude,
      longitude
    } = getCoordinates()
    return await Axios({
      url: storeProfile,
      method: 'GET',
      // headers passes here because localStorage is not available on server side
      headers: {
        'Content-Type': 'application/json',
        'Client-Code': 'vulpix',
        'Accept-Language': 'en',
        'Client-User': 'customer',
        authorization: '',
        'x-access-token': ''
      },
      params: getParams({
        latitude,
        longitude
      })
    })
  },

  getStoreCoupons: async (storeCode, page = 1, limit = 5) => {
    const customerOrderCoupons = `${newOOBaseUrl}/coupon/customer/order`
    return await Axios({
      url: customerOrderCoupons,
      method: 'GET',
      headers: setHeaders(setAuthorizationHeaders()),
      params: {
        storeCode,
        page,
        limit
      }
    })
  }

}

export default storeApis
